const console = global.console;

/**
 * Logger class. Only logs if es.isDeveloper is set to true. e.g.:
 *     es.log.log("test1");
 *     es.log.info("test2");
 *     es.log.warn("test3");
 *     es.log.error("test4");
 */
class Logger {

    private isLoggingEnabled: boolean|undefined;

    private checkLoggingEnabled(): boolean {
        if (this.isLoggingEnabled === undefined) {
            this.isLoggingEnabled = es.util?.localStorage.get("es.enable_logging") === "true";
        }
        return this.isLoggingEnabled;
    }

    toggleLogging() {
        this.isLoggingEnabled = !this.isLoggingEnabled;
        es.util.localStorage.set("es.enable_logging", this.isLoggingEnabled);
        console.log("es.log logging enabled:", this.isLoggingEnabled);
    }

    /**
     * The problem with using this class is that .log and .info msgs show in the dev console as coming from this file.
     * It's not an issue with .warn and .error msgs as they include a stacktrace (so it's trivial to see the msg origin)
     * but by creating an error we can get the stack and also log the src as part of the msg.
     */
    private logWithCorrectSource(method: string, args: IArguments) {
        const platform = (navigator as any).userAgentData?.platform ?? navigator.platform;
        if(es.isNativeApp || /Mac|iPhone|iPod|iPad/i.test(platform)){
            return;
        }
        if(!es.isDeveloper && !this.checkLoggingEnabled()){
            return;
        }

        const original = console[method];
        const stack = new Error().stack.split('\n');
        const caller = stack[3].trim().split(" ").slice(0, 2).join(" ");

        if(method === "info" || method === "log"){
            // Info and log don't show the trace by default. Keep it hidden until expanded
            console.groupCollapsed(...args, caller);
            console.trace(); // This is to have the source maps linking working
            console.groupEnd();
        } else {
            original.apply(console, [...args, caller]);
        }
    }

    trace() {
        this.logWithCorrectSource('trace', arguments);
    }

    debug() {
        this.logWithCorrectSource('debug', arguments);
    }

    log() {
        this.logWithCorrectSource('log', arguments);
    }

    info() {
        this.logWithCorrectSource('info', arguments);
    }

    warn() {
        this.logWithCorrectSource('warn', arguments);
    }

    error() {
        this.logWithCorrectSource('error', arguments);
    }

    table() {
        this.logWithCorrectSource('table', arguments);
    }
}

export default Logger;
